// frontend/src/components/layout/Header.js
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Header = ({ isScrolled, setIsScrolled }) => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [navbarVisible, setNavbarVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => {
      const currentScrollY = window.scrollY;
      
      const scrolled = currentScrollY > 50;
      setIsScrolled(scrolled);
      
      if (currentScrollY < lastScrollY || currentScrollY < 100) {
        setNavbarVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setNavbarVisible(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [lastScrollY, setIsScrolled]);

  return (
    <>
      <header className={`fixed top-0 w-full z-40 transition-all duration-700 ease-out ${
        isScrolled || !isHomePage
          ? 'bg-black/60 backdrop-blur-lg shadow-2xl'
          : 'bg-transparent'
      } ${navbarVisible ? 'translate-y-0' : '-translate-y-full'}`}>
        <nav className="w-full px-4 md:px-8 lg:px-12 py-3 flex items-center justify-between">
          {/* Logo */}
          <img
            src="/logo-new.png"
            alt="Layou Adventures Logo"
            className="hover:scale-110 transition-all duration-300 cursor-pointer h-16 w-auto"
            onClick={() => window.location.href = '/'}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-3">
            {[
              { path: '/', label: 'Home' },
              { path: '/tours', label: 'Tours' },
              { path: '/blog', label: 'Blog' },
              { path: '/contact', label: 'Contact' }
            ].map(({ path, label }) => (
              <Link
                key={path}
                to={path}
                className={`relative px-3 py-1 transition-all duration-500 font-graphik transform hover:scale-105 hover:-translate-y-1 group ${
                  location.pathname === path
                    ? (isScrolled ? 'text-emerald-800' : 'text-gold')
                    : (isScrolled || !isHomePage ? 'text-emerald-800 hover:text-gold' : 'text-white hover:text-gold')
                }`}
              >
                {label}
                <div className={`absolute bottom-0 left-0 w-full h-0.5 transition-all duration-300 ${
                  location.pathname === path
                    ? (isScrolled ? 'bg-emerald-600' : 'bg-gold') + ' scale-x-100'
                    : 'bg-gold scale-x-0 group-hover:scale-x-100'
                }`}></div>
              </Link>
            ))}
            
            {isHomePage && isScrolled && (
              <Link 
                to="/tours" 
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-graphik font-semibold px-6 py-2 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg animate-fade-in"
              >
                View Tours
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-3 rounded-lg hover:bg-white/10 transition-all duration-300"
            aria-label="Toggle menu"
          >
            <div className="w-6 h-6 flex flex-col justify-center space-y-1">
              <span className={`block w-full h-0.5 bg-white transition-all duration-500 ${isMobileMenuOpen ? 'rotate-45 translate-y-1.5' : ''}`}></span>
              <span className={`block w-full h-0.5 bg-white transition-all duration-300 ${isMobileMenuOpen ? 'opacity-0' : 'opacity-100'}`}></span>
              <span className={`block w-full h-0.5 bg-white transition-all duration-500 ${isMobileMenuOpen ? '-rotate-45 -translate-y-1.5' : ''}`}></span>
            </div>
          </button>
        </nav>
      </header>

      {/* Mobile Menu */}
      <div className={`mobile-top-menu ${isMobileMenuOpen ? 'open' : ''}`}>
        <div className="pt-4 pb-6 px-4">
          <div className="flex items-center justify-between mb-6">
            <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center space-x-3">
              <img src="/logo-new.png" alt="logo" className="h-20 w-20 object-contain" />
              <span className="font-playfair text-xl font-bold text-white">LayouAdventures</span>
            </Link>
            <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 text-white">✕</button>
          </div>
          
          <nav className="space-y-3">
            {[
              { path: '/', label: 'Home' },
              { path: '/tours', label: 'Tours' },
              { path: '/blog', label: 'Blog' },
              { path: '/contact', label: 'Contact' }
            ].map(({ path, label }) => (
              <Link
                key={path}
                to={path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block text-lg font-graphik p-3 rounded-md transition-all duration-300 ${
                  location.pathname === path
                    ? 'text-gold bg-white/20 shadow-lg border-l-4 border-gold'
                    : 'text-white hover:bg-white/10 hover:text-gold hover:border-l-4 hover:border-gold/50'
                }`}
              >
                {label}
              </Link>
            ))}
          </nav>
        </div>
      </div>
    </>
  );
};

export default Header;